import { Controller } from "@hotwired/stimulus"

// Handles individual device icons - rendering, selection, and drag behavior
export default class extends Controller {
  static targets = ["icon", "deviceScale"]

  connect() {
    this.enabled = true
    this.scale = 1
    this.icons = new Map() // deviceId -> iconElement mapping
    this.selectedIconId = null
    this.isDragging = false

    this.setupEventListeners()

    // Bind canvas click for deselection
    this.bindCanvasEvents()
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('device:placement:createIcon', this.handleCreateIcon)
    document.removeEventListener('device:placement:deviceUpdated', this.handleDeviceUpdated)
    document.removeEventListener('device:placement:deviceRemoved', this.handleDeviceRemoved)
    document.removeEventListener('device:placement:deviceMarkedForDeletion', this.handleDeviceMarkedForDeletion)
    document.removeEventListener('device:placement:deviceSelected', this.handleDeviceSelected)
    document.removeEventListener('device:placement:modeChanged', this.handleModeChanged)
  }

  setupEventListeners() {
    this.handleCreateIcon = (event) => {
      const { deviceId, deviceData, container } = event.detail
      this.createIcon(deviceId, deviceData, container)
    }

    this.handleDeviceUpdated = (event) => {
      const { deviceId, deviceData } = event.detail
      this.updateIcon(deviceId, deviceData)
    }

    this.handleDeviceRemoved = (event) => {
      const { deviceId } = event.detail
      this.removeIcon(deviceId)
    }

    this.handleDeviceMarkedForDeletion = (event) => {
      const { deviceId } = event.detail
      this.markIconForDeletion(deviceId)
    }

    this.handleDeviceSelected = (event) => {
      const { deviceId } = event.detail
      this.selectIcon(deviceId)
    }

    this.handleModeChanged = (event) => {
      const { mode } = event.detail
      if (mode === 'add') {
        this.clearSelection()
      }
    }

    // Validation failure handling is no longer needed since we use pre-validation

    // Register event listeners
    document.addEventListener('device:placement:createIcon', this.handleCreateIcon)
    document.addEventListener('device:placement:deviceUpdated', this.handleDeviceUpdated)
    document.addEventListener('device:placement:deviceRemoved', this.handleDeviceRemoved)
    document.addEventListener('device:placement:deviceMarkedForDeletion', this.handleDeviceMarkedForDeletion)
    document.addEventListener('device:placement:deviceSelected', this.handleDeviceSelected)
    document.addEventListener('device:placement:modeChanged', this.handleModeChanged)
  }

  bindCanvasEvents() {
    const canvas = document.querySelector('.floor-plan-img')
    if (canvas) {
      canvas.addEventListener('click', this.onCanvasClick.bind(this))
    }
  }

  onCanvasClick(event) {
    // Only handle clicks on the canvas itself, not on device icons
    if (event.target.classList.contains('temp-device-icon')) {
      return
    }

    // Clear selection and switch to add mode
    this.clearSelection()
    this.dispatch('canvasClicked')
  }

  createIcon(deviceId, deviceData, container) {

    if (!container) {
      return
    }

    // Check if icon already exists - prevent duplicates
    if (this.icons.has(deviceId)) {
      const existingIcon = this.icons.get(deviceId)
      existingIcon.style.left = `${deviceData.positionX}px`
      existingIcon.style.top = `${deviceData.positionY}px`
      return existingIcon
    }

    const iconElement = document.createElement('img')
    iconElement.src = this.getDeviceIconPath(deviceData.deviceTypeId)
    iconElement.className = 'device__icon temp-device-icon'
    iconElement.alt = 'device icon'
    iconElement.id = `device-icon-${deviceId}` // Add id for easier testing
    iconElement.dataset.deviceId = deviceId

    // Set position and z-index
    const zIndex = this.calculateZIndex(deviceData.isExisting)
    iconElement.style.cssText = `
      left: ${deviceData.positionX}px; 
      top: ${deviceData.positionY}px; 
      position: absolute; 
      cursor: pointer; 
      z-index: ${zIndex};
    `

    container.appendChild(iconElement)

    this.icons.set(deviceId, iconElement)

    // Verify it was actually added to DOM
    const verification = document.getElementById(iconElement.id)

    // Make draggable and add click handler
    this.makeDraggable(iconElement, deviceId, deviceData)
  }

  updateIcon(deviceId, deviceData) {
    const iconElement = this.icons.get(deviceId)
    if (!iconElement) return

    // Update icon image if device type changed (ensure consistent comparison)
    const deviceTypeId = String(deviceData.deviceTypeId) // Normalize to string for consistency
    const newIconPath = this.getDeviceIconPath(deviceTypeId)
    const currentIconPath = this.normalizeIconPath(iconElement.src)
    const newIconPathNormalized = this.normalizeIconPath(newIconPath)

    if (currentIconPath !== newIconPathNormalized) {
      const oldSrc = iconElement.src
      iconElement.src = newIconPath

      // Add visual feedback to show icon was updated
      iconElement.style.transition = 'transform 0.3s ease'
      iconElement.style.transform = 'scale(1.1)'
      setTimeout(() => {
        iconElement.style.transform = 'scale(1)'
        setTimeout(() => {
          iconElement.style.transition = ''
        }, 300)
      }, 200)
    } else {
    }

    // Add visual indicator for modified devices (if it's existing device that was changed)
    // Use PlacementController's hasDeviceChanged method for accurate comparison
    const placementController = this.getPlacementController()
    if (deviceData.isExisting && placementController && placementController.hasDeviceChanged(deviceData)) {
      iconElement.classList.add('modified')
    } else {
      iconElement.classList.remove('modified')
    }
  }

  // Get PlacementController instance for accessing methods
  getPlacementController() {
    const placementControllerElement = document.querySelector('[data-controller*="device-placement"]')
    return placementControllerElement ? placementControllerElement.devicePlacementController : null
  }

  // Check if device has been modified from its original state
  hasDeviceChanged(deviceData) {
    // This is a simplified check - in real implementation you'd compare with original data
    return deviceData.deviceTypeId || deviceData.deviceModelId || deviceData.placement || deviceData.powerSupplyId
  }

  removeIcon(deviceId) {
    const iconElement = this.icons.get(deviceId)
    if (iconElement) {
      iconElement.remove()
      this.icons.delete(deviceId)
    }

    if (this.selectedIconId === deviceId) {
      this.selectedIconId = null
    }
  }

  markIconForDeletion(deviceId) {
    const iconElement = this.icons.get(deviceId)
    if (iconElement) {
      // Remove any active highlighting first
      iconElement.classList.remove('active')

      // Apply deletion styling
      iconElement.style.opacity = '0.3'
      iconElement.style.filter = 'grayscale(100%)'
      // Set lower z-index for deleted items (below normal devices but above canvas)
      iconElement.style.zIndex = '50'
    }
  }

  selectIcon(deviceId) {

    // Clear previous selection
    this.clearSelection()

    const iconElement = this.icons.get(deviceId)
    this.element.dispatchEvent(new CustomEvent("zoom:disable", { bubbles: true }))
    if (iconElement) {
      this.selectedIconId = deviceId
      this.highlightIcon(iconElement)
    }
  }

  clearSelection() {
    if (this.selectedIconId) {
      const iconElement = this.icons.get(this.selectedIconId)
      if (iconElement) {
        this.unhighlightIcon(iconElement)
      }
      this.selectedIconId = null
    }
  }

  highlightIcon(iconElement) {

    // Use the project's standard active style for device icons
    iconElement.classList.add('active')

    // Get current z-index and increase it
    const currentZIndex = parseInt(iconElement.style.zIndex) || 100
    iconElement.style.zIndex = currentZIndex + 1000
  }

  unhighlightIcon(iconElement) {

    // Remove the project's standard active style
    iconElement.classList.remove('active')

    // Restore original z-index, but only if it was actually highlighted (> 1000)
    const currentZIndex = parseInt(iconElement.style.zIndex) || 1100
    if (currentZIndex > 1000) {
      iconElement.style.zIndex = currentZIndex - 1000
    }
  }

  makeDraggable(iconElement, deviceId, deviceData) {
    let dragStarted = false
    let startX, startY

    iconElement.addEventListener('mousedown', (e) => {
      e.preventDefault()
      if (!this.enabled) return

      this.element.dispatchEvent(new CustomEvent("zoom:disable", { bubbles: true }))

      // Record initial position
      startX = e.clientX
      startY = e.clientY
      dragStarted = false
      this.scale = parseFloat(this.deviceScaleTarget.dataset.scale) || 1

      // Always notify placement controller that this device was clicked for selection
      this.dispatch('iconClicked', { deviceId })

      // Setup drag tracking
      const onMouseMove = (moveEvent) => {
        const deltaX = Math.abs(moveEvent.clientX - startX)
        const deltaY = Math.abs(moveEvent.clientY - startY)

        // Start dragging only if moved more than 5 pixels
        if (!dragStarted && (deltaX > 5 || deltaY > 5)) {
          dragStarted = true
          this.startDrag(iconElement, deviceId, e)
        }
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        this.element.dispatchEvent(new CustomEvent("zoom:enable", { bubbles: true }))

        // If no drag occurred, this was just a click for selection
        if (!dragStarted) {
        }
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    })

    // Prevent default drag behavior
    iconElement.ondragstart = () => false
  }

  startDrag(iconElement, deviceId, startEvent) {
    if (!this.enabled) return
    this.isDragging = true
    iconElement.classList.add('dragging')

    // Get floor plan boundaries
    const imageElement = document.querySelector('.floor-plan-img img.img')
    if (!imageElement) {
      console.error('Cannot find floor plan image for drag boundaries')
      iconElement.classList.remove('dragging')
      this.isDragging = false
      return
    }

    const containerRect = imageElement.getBoundingClientRect()
    const iconRect = iconElement.getBoundingClientRect()

    const shiftX = startEvent.clientX - iconRect.left
    const shiftY = startEvent.clientY - iconRect.top

    const onMouseMove = (e) => {
      let newX = (e.clientX - containerRect.left - shiftX) / this.scale
      let newY = (e.clientY - containerRect.top - shiftY) / this.scale

      // Constrain to boundaries
      newX = Math.max(0, Math.min(newX, containerRect.width - iconElement.offsetWidth))
      newY = Math.max(0, Math.min(newY, containerRect.height - iconElement.offsetHeight))

      // Update visual position
      iconElement.style.left = `${newX}px`
      iconElement.style.top = `${newY}px`

      // Notify placement controller of position change  
      this.dispatch('iconDragged', { deviceId, positionX: newX, positionY: newY })
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)

      iconElement.classList.remove('dragging')
      this.isDragging = false
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  calculateZIndex(isExisting) {
    // Existing devices: base z-index = 100
    // New devices: base z-index = 200 (always on top)
    const baseZIndex = isExisting ? 100 : 200
    return baseZIndex + this.icons.size
  }

  getDeviceIconPath(deviceTypeId) {
    // Get icon path from the hidden input element
    const pathsElement = document.getElementById('device-icon-paths')

    if (!pathsElement) {
      return '/assets/icons/device-placement-ap.svg'
    }

    const normalizedName = this._normalizeDeviceTypeName(this._deviceTypeNameFor(deviceTypeId))

    if (normalizedName.includes('switch')) {
      return pathsElement.dataset.switchPath || '/assets/icons/device-placement-switch.svg'
    }

    if (normalizedName.includes('gateway')) {
      return pathsElement.dataset.gatewayPath || '/assets/icons/device-placement-gateway.svg'
    }

    return pathsElement.dataset.apPath || '/assets/icons/device-placement-ap.svg'
  }

  // Helper to normalize icon paths for comparison (removes hostname, query params)
  normalizeIconPath(fullPath) {
    if (!fullPath) return ''

    try {
      const url = new URL(fullPath, window.location.origin)
      // Return just the pathname to normalize for comparison
      return url.pathname
    } catch (e) {
      // If URL parsing fails, just return the path as-is
      return fullPath
    }
  }

  // Helper method to dispatch custom events
  dispatch(eventName, detail = {}) {
    const fullEventName = `device:icon:${eventName}`
    const event = new CustomEvent(fullEventName, { detail })
    document.dispatchEvent(event)
  }

  toggle(event) {
    this.enabled = event.params.enabled
  }

  _deviceTypeNameFor(deviceTypeId) {
    const value = String(deviceTypeId || '').trim()
    if (!value) return ''

    const option = document.querySelector(`#device_type_id [data-value="${value}"]`)
    if (option) {
      return option.textContent.trim()
    }

    const map = { '1': 'Access Point', '2': 'Switch', '3': 'Gateway' }
    return map[value] || value
  }

  _normalizeDeviceTypeName(name) {
    return (name || '').toString().trim().toLowerCase()
  }
}
